import React, { useState, useEffect } from 'react';
import HeadlessModal from '@/components/shared/HeadlessModal';
import Input from '@/components/ui/Input';
import Label from '@/components/ui/Label';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { sendPhoneOtp, verifyPhoneOtp } from '@/clients/customer/auth';
import { useUserStore } from '@/stores/customer/user';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import useAppLocale from '@/hooks/useAppLocale';
import { countryCodesForMobile } from '@/constant/configs';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userPhone?: string | null;
  phoneVerified?: boolean;
  onVerified?: () => void;
}

export default function PhoneVerificationModal({
  isOpen,
  onClose,
  userPhone,
  phoneVerified = false,
  onVerified,
}: PhoneVerificationModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 5 minutes in seconds
  const [isTimerActive, setIsTimerActive] = useState(false);

  const { countryCode } = useAppLocale();

  const getUpdatedUser = useUserStore((state) => state.getUpdatedUser);

  const t = useTranslations('storefront.profile');
  const t_common = useTranslations('common');

  // Timer for OTP countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isTimerActive) {
      setIsTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  // utility to strip german country code from phone
  const normalizeUserPhone = (phone: string) => {
    let num = phone.replace(/[^0-9]/g, '');
    // remove leading 49 if present
    if (num.startsWith('49')) {
      num = num.slice(2);
    }
    return num;
  };

  const resetState = () => {
    setPhoneNumber('');
    setOtp('');
    setOtpSent(false);
    setTimeLeft(60);
    setIsTimerActive(false);
    setPhoneError(null);
  };

  // Reset state when modal closes or pre-fill phone if exists
  useEffect(() => {
    if (!isOpen) {
      resetState();
    } else if (isOpen && userPhone) {
      // Pre-fill phone number if user has one and it's not verified
      setPhoneNumber(normalizeUserPhone(userPhone));
    }
  }, [isOpen, userPhone, phoneVerified]);

  const isValidGermanPhone = (num: string): boolean => {
    // basic validation: digits only, length 9-14
    return /^[0-9]{9,14}$/.test(num);
  };

  const handleSendOtp = async () => {
    setOtp('');
    if (!phoneNumber.trim()) {
      setPhoneError(t('invalid_phone_err_message'));
      return;
    }

    if (!isValidGermanPhone(phoneNumber.trim())) {
      setPhoneError(t('invalid_phone_err_message'));
      return;
    }

    setPhoneError(null);
    setPhoneLoading(true);
    try {
      // API call to send OTP - adjust endpoint as needed
      const response = await sendPhoneOtp({ phone: phoneNumber });
      if (response.status === 200) {
        setPhoneError(null);
        toast.success(response?.data?.message);
        setOtpSent(true);
        setTimeLeft(60);
        setIsTimerActive(true);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'An error occurred';
      setPhoneError(message);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setPhoneError(t('please_enter_otp'));
      return;
    }

    setPhoneLoading(true);
    try {
      // API call to verify OTP - adjust endpoint as needed
      const response = await verifyPhoneOtp({ phone: phoneNumber, otp });
      if (response.status === 200) {
        setPhoneError(null);
        toast.success(response?.data.message);
        setIsTimerActive(false);
        // update store/local user data with new phone status
        await getUpdatedUser();
        if (onVerified) {
          onVerified();
        }
        onClose();
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'An error occurred';
      setPhoneError(message);
    } finally {
      setPhoneLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const phoneCc =
    countryCodesForMobile[
      countryCode.toLowerCase() as keyof typeof countryCodesForMobile
    ] ?? '+49';

  return (
    <HeadlessModal
      isOpen={isOpen}
      closeModal={() => {
        resetState();
        onClose();
      }}
      closeOnBackdropClick={false}
      dialogPanelClassName="md:w-[70%] lg:w-[50%] lg:max-w-[650px] flex justify-center items-center"
      dialogBodyClass="overflow-y-auto"
    >
      <div className="md:mt-10">
        <h2 className="mb-4 text-center text-h3 font-medium">
          {t('verify_phone_number')}
        </h2>

        {!otpSent ? (
          <>
            <div className="mb-6 space-y-2">
              <Label htmlFor="modal-phone">{t('enter_phone_number')}</Label>
              <div className="flex w-full items-center">
                <div className="flex h-[40.19px] w-[60px] items-center justify-center rounded-s-[6px] border border-e-0 border-[#D2D5DA] bg-gray-50 px-[5px]">
                  <span className="text-sm font-medium text-gray-700">+49</span>
                </div>
                <Input
                  id="modal-phone"
                  type="text"
                  placeholder="1234567890"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneError(null);
                    setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
                  }}
                  disabled={phoneLoading}
                  className="rounded-s-none border-s-0"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {t('valid_phone_format_hint')}
              </p>
              {phoneError && (
                <p role="alert" className="mt-3 text-sm text-red-500">
                  {phoneError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={onClose}
                className="mt-10 flex items-center justify-start rounded-full bg-gray-300 px-8 py-2 text-sm font-normal text-black shadow"
              >
                {t_common('cancel')}
              </button>
              <button
                onClick={handleSendOtp}
                disabled={phoneLoading || !phoneNumber.trim()}
                className="mt-10 flex items-center justify-start rounded-full bg-primary px-8 py-2 text-sm font-normal text-white shadow disabled:bg-gray-300"
              >
                {phoneLoading ? <LoadingSpinner /> : t('send_otp')}
              </button>
            </div>
          </>
        ) : (
          <>
            {' '}
            <div className="mb-2 text-center text-sm text-gray-700">
              {t('otp_has_sent_to')}
              <span className="font-medium">
                {phoneCc} {phoneNumber}
              </span>
            </div>{' '}
            <div className="mb-6 space-y-3">
              <Label htmlFor="modal-otp">{t('enter_otp')}</Label>
              <div className="">
                <Input
                  id="modal-otp"
                  type="text"
                  placeholder={t('enter_4_digit_otp')}
                  value={otp}
                  onChange={(e) => {
                    setPhoneError(null);
                    setOtp(e.target.value);
                  }}
                  disabled={phoneLoading}
                  maxLength={6}
                  className={cn(phoneError && 'border-red-500')}
                />
                {phoneError && (
                  <p role="alert" className="mt-3 text-sm text-red-500">
                    {phoneError}
                  </p>
                )}
              </div>
              {timeLeft > 0 ? (
                <div className="">
                  <p className="text-center text-sm font-medium text-primary">
                    {t('time_remaining')}: {formatTime(timeLeft)}
                  </p>
                </div>
              ) : null}

              {timeLeft >= 0 ? (
                <div className="mt-2 flex justify-center">
                  <button
                    onClick={handleSendOtp}
                    disabled={phoneLoading || timeLeft > 0}
                    className="bg-secondary hover:bg-secondary-dark rounded-full bg-primary px-6 py-2 text-sm font-medium text-white shadow disabled:bg-gray-300"
                  >
                    {t('resend_otp')}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={onClose}
                className="mt-10 flex items-center justify-start rounded-full bg-gray-300 px-8 py-2 text-sm font-normal text-black shadow"
              >
                {t_common('cancel')}
              </button>
              <button
                onClick={handleVerifyOtp}
                // disabled={phoneLoading || !otp.trim() || timeLeft === 0}
                className="mt-10 flex items-center justify-start rounded-full bg-primary px-8 py-2 text-sm font-normal text-white shadow disabled:bg-gray-300"
              >
                {phoneLoading ? <LoadingSpinner /> : t('verify_now')}
              </button>
            </div>
          </>
        )}
      </div>
    </HeadlessModal>
  );
}
